"use client"

import { useEffect, useState } from "react"
import { apiUrl, authHeaders } from "@/lib/api"

interface TickerProduct {
  id: number
  title: string
  store?: { name?: string } | null
  discount_percentage?: number | string
  price_current?: string | number
  created_at?: string
  url?: string
}

function timeAgo(iso?: string): string {
  if (!iso) return ""
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (sec < 60) return `${sec}s`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m`
  return `${Math.floor(min / 60)}h`
}

function formatPrice(val?: string | number): string {
  if (!val) return "—"
  const n = typeof val === "string" ? parseFloat(val) : val
  if (isNaN(n) || n <= 0) return "—"
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })
}

const STORE_STYLES: Record<string, { bg: string; text: string }> = {
  kabum: {
    bg: "bg-orange-500/10 dark:bg-orange-500/15",
    text: "text-orange-700 dark:text-orange-400",
  },
  terabyte: {
    bg: "bg-blue-500/10 dark:bg-blue-500/15",
    text: "text-blue-700 dark:text-blue-400",
  },
}

function storeStyle(name?: string) {
  const lower = (name || "").toLowerCase()
  for (const key of Object.keys(STORE_STYLES)) {
    if (lower.includes(key)) return STORE_STYLES[key]
  }
  return { bg: "bg-muted/40", text: "text-foreground/70" }
}

function discountStyle(pct: number): string {
  if (pct >= 60) return "text-rose-500 dark:text-rose-400 font-semibold"
  if (pct >= 30) return "text-[var(--brand-amber)] font-semibold"
  if (pct >= 15) return "text-[var(--brand-amber)] font-medium"
  return "text-muted-foreground"
}

function TickerItem({ p }: { p: TickerProduct }) {
  const desconto = parseFloat(String(p.discount_percentage || 0))
  const lojaNome = p.store?.name || ""
  const sStyle = storeStyle(lojaNome)
  return (
    <a
      href={p.url || "#"}
      target={p.url ? "_blank" : undefined}
      rel="noopener noreferrer"
      className="inline-flex shrink-0 items-center gap-2.5 px-6 text-[13px] transition-opacity hover:opacity-100"
    >
      {lojaNome && (
        <span className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-[0.14em] ${sStyle.bg} ${sStyle.text}`}>
          {lojaNome}
        </span>
      )}
      <span className="max-w-[280px] truncate text-foreground/85">{p.title}</span>
      {desconto > 0 && (
        <span className={`shrink-0 tabular-nums ${discountStyle(desconto)}`}>
          −{desconto.toFixed(0)}%
        </span>
      )}
      <span className="shrink-0 text-foreground tabular-nums">{formatPrice(p.price_current)}</span>
      <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums">{timeAgo(p.created_at)}</span>
      <span className="shrink-0 text-border">·</span>
    </a>
  )
}

export default function LiveTicker() {
  const [items, setItems] = useState<TickerProduct[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null
    fetch(`${apiUrl()}/products/?ordering=-created_at&page_size=30`, { headers: authHeaders(token) })
      .then((r) => r.json())
      .then((data) => {
        const lista = (data.results || (Array.isArray(data) ? data : [])) as TickerProduct[]
        setItems(lista)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="relative overflow-hidden rounded-lg border border-border/40 bg-muted/10 py-2.5">
        <div className="h-5 w-full animate-pulse rounded bg-muted/30" />
      </div>
    )
  }

  if (items.length === 0) return null

  // Duplicate for seamless loop
  const doubled = [...items, ...items]

  return (
    <div className="group relative overflow-hidden rounded-lg border border-border/40 bg-muted/10 py-3">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-background via-background/80 to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-background via-background/80 to-transparent"
      />

      <div className="relative flex items-center gap-3 pl-4">
        <div className="z-20 inline-flex flex-shrink-0 items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.16em] text-emerald-700 backdrop-blur dark:text-emerald-400">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-60" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
          </span>
          ao vivo
        </div>

        <div className="flex w-full min-w-0 flex-1 overflow-hidden">
          <div className="animate-marquee flex shrink-0 items-center whitespace-nowrap">
            {doubled.map((p, i) => (
              <TickerItem key={`${p.id}-${i}`} p={p} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
